import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, markSession, clearSession, hasSession } from './api.js';
import { effectiveLevel } from './rbac.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reidrata a sessão a partir do JWT, se houver.
  useEffect(() => {
    let active = true;
    async function hydrate() {
      if (!hasSession()) {
        setLoading(false);
        return;
      }
      try {
        const me = await api.me();
        if (active) setUser(me?.data ?? me);
      } catch {
        // Access token pode ter expirado — tenta renovar via refresh token
        // (cookie HttpOnly) antes de descartar a sessão.
        try {
          await api.refresh();
          const me = await api.me();
          if (active) setUser(me?.data ?? me);
        } catch {
          clearSession();
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    hydrate();
    return () => {
      active = false;
    };
  }, []);

  // O token não é mais manipulado aqui: vem em cookie HttpOnly na resposta do
  // login (spec 008). `manterConectado` vai para o backend, que decide se os
  // cookies persistem ou morrem ao fechar o browser.
  const login = useCallback(async (usuarioLogin, senha, manterConectado = false) => {
    await api.login(usuarioLogin, senha, manterConectado);
    markSession(manterConectado);
    const me = await api.me();
    setUser(me?.data ?? me);
  }, []);

  const googleLogin = useCallback(async (code, redirectUri, manterConectado = false) => {
    await api.googleSSOLogin(code, redirectUri, manterConectado);
    markSession(manterConectado);
    const me = await api.me();
    setUser(me?.data ?? me);
  }, []);

  // Re-busca o /auth/me e atualiza o usuário (após self-edit do perfil).
  const refreshUser = useCallback(async () => {
    const me = await api.me();
    setUser(me?.data ?? me);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignora erro de logout remoto */
    }
    clearSession();
    setUser(null);
  }, []);

  // Nível de acesso: maior papel acumulável no JWT (AUD-12).
  const level = effectiveLevel(user);

  return (
    <AuthContext.Provider value={{ user, level, loading, login, googleLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
